import { skipToken } from '@reduxjs/toolkit/query'
import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useGetMyContractorsQuery } from '../../../../store/api/contractors-api'
import { useGetMyCuratorShipmentsQuery } from '../../../../store/api/shipments-api'
import { useUser } from '../../../../hooks/use-user'
import { EndpointQueryParameters } from '../../../../types/strapi/strapi'
import { calcManagerMonthlyStats, mapShipmentToRow } from '../helpers/calc-manager-monthly-stats'

function buildContractorsQueryParams(): EndpointQueryParameters {
    return {
        populate: {
            curators: {
                populate: {
                    user: true,
                },
            },
        },
        pagination: { page: 1, pageSize: 500 },
    }
}

function buildShipmentsQueryParams(month: Dayjs): EndpointQueryParameters {
    const monthStart = month.startOf('month').toISOString()
    const monthEnd = month.endOf('month').toISOString()

    return {
        filters: {
            createdAt: { $gte: monthStart, $lte: monthEnd },
        },
        populate: {
            client: {
                fields: ['name'],
            },
            calculated: true,
            calculatedOnActual: true,
            user_audit: {
                populate: {
                    created_by_permissions_user: true,
                },
            },
            blankPreviewFiles: true,
        },
        sort: ['createdAt:desc'],
        pagination: { page: 1, pageSize: 500 },
    }
}

export default function useManagerShipments(month: Dayjs) {
    const user = useUser()
    const userDocumentId = user?.documentId

    const contractorsQueryParams = useMemo(() => buildContractorsQueryParams(), [])

    const {
        data: contractorsData,
        isLoading: isContractorsLoading,
        isFetching: isContractorsFetching,
    } = useGetMyContractorsQuery(contractorsQueryParams, {
        skip: !userDocumentId,
    })

    const clientPercentByDocId = useMemo(() => {
        const map = new Map<string, number>()

        if (!userDocumentId) {
            return map
        }

        for (const contractor of contractorsData?.data ?? []) {
            const curator = contractor.curators?.find(
                item => item.user?.documentId === userDocumentId
            )

            if (
                contractor.documentId &&
                curator?.percent !== undefined &&
                curator.percent !== null
            ) {
                map.set(contractor.documentId, curator.percent)
            }
        }

        return map
    }, [contractorsData?.data, userDocumentId])

    const shipmentsQueryParams = useMemo(() => {
        if (!userDocumentId) {
            return skipToken
        }

        return buildShipmentsQueryParams(month)
    }, [userDocumentId, month])

    const {
        data: shipmentsData,
        isLoading: isShipmentsLoading,
        isFetching: isShipmentsFetching,
    } = useGetMyCuratorShipmentsQuery(shipmentsQueryParams)

    const rows = useMemo(() => {
        if (!userDocumentId) {
            return []
        }

        return (shipmentsData?.data ?? [])
            .map(shipment => mapShipmentToRow(shipment, userDocumentId, clientPercentByDocId))
            .filter((row): row is NonNullable<typeof row> => row != null)
    }, [shipmentsData?.data, userDocumentId, clientPercentByDocId])

    const stats = useMemo(() => calcManagerMonthlyStats(rows), [rows])

    return {
        rows,
        isLoading:
            isContractorsLoading ||
            isContractorsFetching ||
            isShipmentsLoading ||
            isShipmentsFetching,
        stats,
    }
}
